import { describeStepFailure, isErrorStepStatus, pipelineRunStatusRu, PIPELINE_ERROR_STEP_STATUSES, } from '@wit-support/admin-contracts';
import { statusLabelRu } from './status-labels.js';
/**
 * Single source of truth for the Russian (UI) labels of pipeline node types and
 * for the human one-liner of a pipeline STEP outcome.
 *
 * Why this file exists (D19): the run-history rail used to show an operator only
 * the machine facts — `pipeline_run.error` = `failed at node no_user` plus a raw
 * `result` JSON dump — so a run whose EMAIL SEND died on `invalid_grant` read as
 * "пользователь не найден" (21 prod runs were mis-read exactly that way). The
 * node id is the graph author's label, not the cause.
 *
 * Home + shape copy {@link ./status-labels.ts} (`STATUS_LABEL_RU` +
 * `statusLabelRu`): a plain dictionary of machine value → RU string, and a
 * lookup function with a MANDATORY fallback to the raw value, the same
 * `GATE_REASON_RU[reason] ?? reason` idiom the inbox already uses for gate
 * reasons. Only the *display* string is localized — machine values are never
 * translated.
 *
 * The node-type labels are NOT invented here: they are lifted verbatim out of
 * the pipeline editor's `Select` (`packages/admin-module-pipelines/src/client/
 * forms/pipeline-dialog.tsx`), which now renders them FROM these arrays, so the
 * editor and the rail can no longer drift apart.
 */
/** Condition node types, in editor order (drives the editor's `Select`). */
export const PIPELINE_CONDITION_TYPES = [
    'user_identified',
    'has_active_subscription',
    'field_match',
];
/** Action node types, in editor order (drives the editor's `Select`). */
export const PIPELINE_ACTION_TYPES = [
    'send_template_email',
    'send_custom_email',
    'cancel_subscription',
    'delete_account',
    'set_conversation_status',
    'collect_input',
    'resolve_identity',
];
/**
 * Node type → RU label. Keyed by plain `string` (not the engine's `ActionType`
 * union) because it is also fed historical `pipeline_run_step.type` values read
 * back from the DB, which may name a node type the current engine no longer has.
 */
export const PIPELINE_NODE_TYPE_LABEL_RU = {
    // Conditions
    user_identified: 'Пользователь определён',
    has_active_subscription: 'Есть активная подписка',
    field_match: 'Сравнение поля',
    // Actions
    send_template_email: 'Отправить шаблон письма',
    send_custom_email: 'Отправить своё письмо',
    cancel_subscription: 'Отменить подписку',
    delete_account: 'Удалить аккаунт (GDPR)',
    set_conversation_status: 'Сменить статус диалога',
    collect_input: 'Собрать данные у клиента',
    resolve_identity: 'Привязать по email',
    // Not nodes any graph contains: synthetic markers the worker writes when the
    // park ended for a reason no node expresses. Both must be named here — an
    // unnamed marker renders as its raw machine id in the rail.
    //  • the operator answered by hand while the run was parked, which is exactly
    //    why the run left `waiting` without executing a node;
    //  • the customer's answer arrived as a NEW letter, filed by the threading
    //    resolver in another dialog of the same contact.
    operator_takeover: 'Оператор ответил вручную',
    resumed_from_other_conversation: 'Ответ пришёл из другого диалога',
};
/**
 * Node type → (routing outcome → RU label), for the node types that branch on a
 * domain outcome. Lifted verbatim from the pipeline editor's routing lists
 * (`CANCEL_OUTCOMES` / `DELETE_ACCOUNT_OUTCOMES` / `COLLECT_INPUT_OUTCOMES` /
 * `RESOLVE_IDENTITY_OUTCOMES` in `pipeline-dialog.tsx`) so the visual builder and
 * the editor say the same words while both exist.
 *
 * These are the words for the BRANCH an author wires («куда идти, если…»), not
 * the run-history verdict — that one is {@link PIPELINE_STEP_STATUS_LABEL_RU}.
 * `queued` keeps the D13 wording on purpose: the upstream only acknowledges a
 * deletion request, and a branch label reading «удалено» invites routing it into
 * a confirmation email.
 *
 * Keyed by plain `string` for the same reason as {@link PIPELINE_NODE_TYPE_LABEL_RU}
 * (this package does not depend on the engine); totality against the engine's
 * outcome enums is asserted where both are visible —
 * `admin-module-pipelines/src/client/graph/node-spec.test.ts`.
 */
export const PIPELINE_NODE_OUTCOME_LABEL_RU = {
    cancel_subscription: {
        cancelled: 'Отменена',
        already_cancelled: 'Уже была отменена',
        no_subscription: 'Нет подписки',
        failed: 'Ошибка',
        skipped_no_adapter: 'Пропущено (нет адаптера)',
    },
    delete_account: {
        queued: 'Принято, не подтверждено',
        already_deleted: 'Уже удалён',
        no_user: 'Нет привязки к аккаунту',
        failed: 'Ошибка',
    },
    collect_input: {
        collected: 'Данные собраны',
        escalated: 'Эскалация / таймаут',
    },
    resolve_identity: {
        resolved: 'Личность найдена',
        unresolved: 'Не найдена',
    },
};
/** Safe lookup of a routing outcome's RU label; unknown pairs render the raw outcome. */
export function pipelineOutcomeLabelRu(type, outcome) {
    return PIPELINE_NODE_OUTCOME_LABEL_RU[type]?.[outcome] ?? outcome;
}
/**
 * `field_match` operator → RU words, as a predicate reads in a sentence
 * («Число успешных списаний больше 4»). Lifted from the editor's
 * `FIELD_OPERATOR_LABELS`.
 */
export const PIPELINE_FIELD_OPERATOR_LABEL_RU = {
    exists: 'заполнено',
    not_exists: 'не заполнено',
    equals: 'равно',
    not_equals: 'не равно',
    gt: 'больше',
    gte: 'больше или равно',
    lt: 'меньше',
    lte: 'меньше или равно',
    contains: 'содержит',
    in: 'входит в список',
    is_true: 'истина',
    is_false: 'ложь',
};
/** `field_match` source profile → RU label. Lifted from the editor's `FIELD_SOURCE_LABELS`. */
export const PIPELINE_FIELD_SOURCE_LABEL_RU = {
    identity: 'Личность',
    payment: 'Оплата',
    billing: 'Биллинг',
};
/**
 * Email `delay` preset → RU phrase as it reads after the letter («… через 3 дня»).
 * The editor's picker spells the same presets as «+3 дня»; this is the sentence
 * form for summaries and «читать как текст».
 */
export const PIPELINE_DELAY_LABEL_RU = {
    '+30m': 'через 30 минут',
    '+1d': 'через 1 день',
    '+2d': 'через 2 дня',
    '+3d': 'через 3 дня',
    '+4d': 'через 4 дня',
};
/**
 * `collect_input` extract-field kind → RU label («что достать из ответа»).
 * Lifted from the old editor's Select (`pipeline-dialog.tsx`). Plain `string`
 * keys for the same reason as the dictionaries above; totality against the
 * engine's `ExtractFieldKindSchema` is asserted in `admin-module-pipelines`
 * (`editor/inspector/forms/collect-input-form.test.tsx`).
 */
export const PIPELINE_EXTRACT_KIND_LABEL_RU = {
    email: 'Email',
    card_last4: 'Последние 4 цифры карты',
    text: 'Текст',
};
/**
 * Uppercase the first character and touch NOTHING else. Deliberately not
 * `./lib/capitalize.ts`: that one is a machine-token humanizer — it lowercases
 * the tail and eats underscores, which would turn a quoted provider cause
 * («Gmail отозвал токен (invalid_grant)») into mush.
 */
const upperFirst = (s) => (s ? s[0].toUpperCase() + s.slice(1) : s);
/**
 * RUN status → RU label for a BADGE.
 *
 * The words themselves are NOT defined here: `PIPELINE_RUN_STATUS_RU` in
 * `@wit-support/admin-contracts` is the single source, shared with the Telegram
 * card (a Node process that cannot depend on this React package). This layer
 * adds only what is specific to an admin badge — sentence capitalization and a
 * tone — so a new run status is still named in exactly one place.
 *
 * Why this indirection exists at all: the conversation rail and the
 * pipeline-runs page each used to carry a private copy, and both had drifted —
 * neither knew `waiting`, so a parked run printed the raw English word in grey.
 * The Telegram card carried a third.
 */
/** Badge tone per run status. `waiting` is `info`, not a warning: nothing is wrong. */
export const PIPELINE_RUN_STATUS_VARIANT = {
    running: 'info',
    waiting: 'info',
    completed: 'success',
    failed: 'danger',
    // Neutral, NOT danger: a merge-cancelled run is terminal and expected — the
    // engine collapsed a duplicate. `hadError` stays false for it (it is neither
    // `failed` nor carries an error step), so the rail must not paint it red
    // either, or the row would read as a defect the operator has to chase.
    cancelled: 'neutral',
};
/**
 * Badge label for a run status: the shared phrase, capitalized. An UNKNOWN
 * status is passed through untouched — `pipelineRunStatusRu` returns the raw
 * machine value for those, and prettifying it would disguise an untranslated
 * status as a real label.
 */
export function pipelineRunStatusLabelRu(status) {
    const phrase = pipelineRunStatusRu(status);
    return phrase === status ? status : upperFirst(phrase);
}
/** Safe lookup of a run status's badge tone; unknown statuses stay neutral. */
export function pipelineRunStatusVariant(status) {
    return PIPELINE_RUN_STATUS_VARIANT[status] ?? 'neutral';
}
/**
 * Per-STEP status → short RU verdict for a badge. Same de-duplication story as
 * {@link pipelineRunStatusLabelRu}: the conversation rail's timeline and the
 * pipeline-runs page each kept a copy, and the runs page's copy stopped at
 * `outcome_no_subscription` — every later outcome (`outcome_queued`,
 * `outcome_collected`, `outcome_escalated`, `outcome_resolved`, …) rendered raw.
 *
 * The BADGE is the short verdict; the human sentence beside it comes from
 * {@link pipelineStepSummaryRu}, which also knows the node type and `result`.
 */
export const PIPELINE_STEP_STATUS_LABEL_RU = {
    ok: 'OK',
    branch_true: 'ветка: да',
    branch_false: 'ветка: нет',
    skipped: 'пропущен',
    waiting: 'ждём ответа',
    failed: 'ошибка',
    outcome_failed: 'ошибка',
    outcome_skipped_no_adapter: 'не выполнено: нет адаптера',
    outcome_cancelled: 'отменено',
    outcome_already_cancelled: 'уже отменено',
    outcome_no_subscription: 'нет подписки',
    // D13: the upstream only ACKs a deletion request — nothing confirms it went
    // through, so this must not read (or look) like a done deal.
    outcome_queued: 'принято, не подтверждено',
    outcome_already_deleted: 'уже удалено',
    outcome_no_user: 'пользователь не найден',
    outcome_collected: 'данные получены',
    outcome_escalated: 'передано оператору',
    outcome_resolved: 'email привязан',
    outcome_unresolved: 'не привязано',
};
export const PIPELINE_STEP_STATUS_VARIANT = {
    ok: 'success',
    branch_true: 'info',
    branch_false: 'info',
    skipped: 'neutral',
    waiting: 'info',
    failed: 'danger',
    outcome_failed: 'danger',
    outcome_skipped_no_adapter: 'danger',
    outcome_cancelled: 'success',
    outcome_already_cancelled: 'neutral',
    outcome_no_subscription: 'neutral',
    // Not 'success': an unconfirmed request is in flight, not finished (D13).
    outcome_queued: 'info',
    outcome_already_deleted: 'neutral',
    outcome_no_user: 'neutral',
    outcome_collected: 'success',
    outcome_escalated: 'info',
    outcome_resolved: 'success',
    outcome_unresolved: 'neutral',
};
// Compile-time guard: every canonical error status MUST be mapped to `danger`.
// Lives with the map (not at a call site) so BOTH consumers inherit it.
PIPELINE_ERROR_STEP_STATUSES.forEach((s) => {
    if (PIPELINE_STEP_STATUS_VARIANT[s] !== 'danger') {
        throw new Error(`error status ${s} must map to danger`);
    }
});
/** Safe lookup of a step status's RU verdict; unknown statuses render as-is. */
export function pipelineStepStatusLabelRu(status) {
    return PIPELINE_STEP_STATUS_LABEL_RU[status] ?? status;
}
/** Safe lookup of a step status's badge tone; unknown statuses stay neutral. */
export function pipelineStepStatusVariant(status) {
    return PIPELINE_STEP_STATUS_VARIANT[status] ?? 'neutral';
}
/** Safe lookup of a node type's RU label; unknown types render as-is. */
export function pipelineNodeTypeLabelRu(type) {
    return PIPELINE_NODE_TYPE_LABEL_RU[type] ?? type;
}
/**
 * Machine cause marker → RU phrase, matched as a case-insensitive SUBSTRING of
 * the cause text (`describeStepFailure`'s output), first match wins.
 *
 * Substring, not equality: a cause is a composed line — `invalid_grant: Token
 * has been expired or revoked.` (Gmail, after `describeGoogleError` folds in the
 * provider text), `cancel_subscription: not cancelled (status=no_subscription)`,
 * `1 cancelled, 1 failed: Subscription has no gateway external id`. Equality
 * would match none of them, and pinning the full text would break the moment a
 * provider reworded its message.
 *
 * The list is deliberately SHORT and ordered specific → general: on prod all 109
 * failures are two families (email send + subscription cancel), plus the
 * runner's own synthetic failures. An unmatched cause is shown RAW — never
 * hidden, never guessed at.
 */
const CAUSE_RU = [
    ['invalid_grant', 'Gmail отозвал токен (invalid_grant) — канал нужно переподключить'],
    ['no gateway external id', 'у подписки нет идентификатора в платёжном шлюзе'],
    ['no_subscription', 'у клиента нет активной подписки'],
    ['contact has no wit_user_id', 'контакт не связан с пользователем приложения'],
    ['app-service error', 'сервис приложения ответил ошибкой'],
    ['reply data unavailable', 'нет данных для подстановки в шаблон письма'],
    ['no value in context', 'в контексте прогона нет значения для привязки'],
    ['max_steps_exceeded', 'превышен лимит шагов пайплайна'],
    ['node_not_found', 'узел графа не найден'],
    ['cycle_detected', 'граф зациклился'],
];
/**
 * Retry exhaustion, as the account-deletion adapter writes it (D13):
 * `<underlying detail>, gave up after 3 attempts`. It WRAPS a cause rather than
 * being one, so it is peeled off and re-attached as a suffix instead of living
 * in {@link CAUSE_RU} — otherwise a flat marker would swallow the underlying
 * detail («что именно ответил сервис») or, placed lower, hide the fact that the
 * failure is persistent and not a one-off blip. Adapter-neutral wording: the
 * `gave up after N attempts` tail is generic, any retrying adapter may write it.
 */
const RETRY_EXHAUSTED_RE = /^(.*?),\s*gave up after \d+ attempts?\.?$/i;
/**
 * RU phrase for a machine cause text, or `null` when the cause is not in the
 * dictionary (the caller then shows the raw text — see {@link CAUSE_RU}).
 */
export function pipelineCauseRu(cause) {
    const exhausted = RETRY_EXHAUSTED_RE.exec(cause)?.[1]?.trim();
    if (exhausted) {
        // The inner cause keeps its own translation (or stays raw); the exhaustion
        // is the part the operator acts on — it means "persistent", not "transient".
        return `${pipelineCauseRu(exhausted) ?? exhausted} — повторы исчерпаны`;
    }
    const haystack = cause.toLowerCase();
    for (const [marker, ru] of CAUSE_RU) {
        if (haystack.includes(marker.toLowerCase()))
            return ru;
    }
    return null;
}
/** Node type → what a FAILURE of that node means, in operator words. */
const FAILURE_RU = {
    send_template_email: 'не удалось отправить письмо',
    send_custom_email: 'не удалось отправить письмо',
    cancel_subscription: 'не удалось отменить подписку',
    delete_account: 'не удалось удалить аккаунт',
    set_conversation_status: 'не удалось сменить статус диалога',
    collect_input: 'не удалось спросить клиента',
    resolve_identity: 'не удалось привязать по email',
};
/**
 * Step status → RU outcome phrase for the NON-error statuses. `failed` /
 * `outcome_failed` / `outcome_skipped_no_adapter` are absent on purpose: they go
 * through the cause path in {@link pipelineStepSummaryRu}.
 */
const OUTCOME_RU = {
    ok: 'выполнено',
    branch_true: 'условие выполнено',
    branch_false: 'условие не выполнено',
    skipped: 'узел уже посещался — шаг пропущен',
    waiting: 'ждём ответа клиента',
    outcome_cancelled: 'подписка отменена',
    outcome_already_cancelled: 'подписка уже была отменена',
    outcome_no_subscription: 'активной подписки не было',
    // D13: a `queued` deletion is an ACCEPTED request, not a confirmed deletion —
    // the upstream only acks it. Reads as such everywhere, incl. the badge.
    outcome_queued: 'запрос на удаление принят, удаление не подтверждено',
    outcome_already_deleted: 'аккаунт уже был удалён',
    outcome_no_user: 'пользователь в приложении не найден',
    outcome_collected: 'данные получены от клиента',
    outcome_escalated: 'передано оператору',
    outcome_resolved: 'email привязан к пользователю',
    outcome_unresolved: 'привязать по email не удалось',
};
/** Per-(type, status) overrides where the generic phrase would be vague. */
const OUTCOME_BY_TYPE = {
    user_identified: { branch_true: 'клиент опознан', branch_false: 'клиент не опознан' },
    has_active_subscription: {
        branch_true: 'подписка активна',
        branch_false: 'активной подписки нет',
    },
    collect_input: { waiting: 'вопрос отправлен, ждём ответа клиента' },
};
const isRecord = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
/**
 * Result-derived detail for the `ok` statuses, where the status alone does not
 * say what happened: an email either went out or was parked for later send, and
 * a status change is only meaningful with the status it set.
 */
function okPhrase(step) {
    const result = isRecord(step.result) ? step.result : null;
    switch (step.type) {
        case 'send_template_email':
        case 'send_custom_email':
            if (result?.scheduledEmailId)
                return 'письмо поставлено в очередь на отложенную отправку';
            if (result?.messageId)
                return 'письмо отправлено';
            return null;
        case 'set_conversation_status':
            return typeof result?.status === 'string'
                ? `статус диалога → «${statusLabelRu(result.status)}»`
                : null;
        case 'cancel_subscription':
            return result?.cancel === 'cancelled' ? 'подписка отменена' : null;
        case 'collect_input':
            return result?.messageId ? 'вопрос отправлен клиенту' : null;
        // Synthetic marker, `ok` by construction: the generic «выполнено» would say
        // nothing, and WHY the park was found at all is the whole point of the line.
        case 'resumed_from_other_conversation':
            return 'парковка найдена по контакту, а не по диалогу';
        default:
            return null;
    }
}
/**
 * D-предсбор. What a `collect_input` step says when the letter was NOT the story.
 *
 * The status alone misleads in both cases: `outcome_collected` reads as «данные
 * получены от клиента», which an operator scanning the rail takes to mean «мы
 * спросили, он ответил» — while nothing was sent at all and the answer came from a
 * letter that had been lying unprocessed. And an escalation whose reason is «мы не
 * смогли прочитать уже полученный ответ» is not the same event as «клиент не
 * ответил», though both land on `outcome_escalated`.
 */
function collectInputPhraseRu(step) {
    const result = isRecord(step.result) ? step.result : null;
    if (!result)
        return null;
    if (result.precollected === true) {
        return 'вопрос не отправляли — данные взяты из уже полученного ответа';
    }
    if (result.suppressed === 'question_already_asked') {
        return 'этот вопрос уже задавали, а разобрать ответ не удалось — передано оператору';
    }
    if (result.suppressed === 'already_processed_reply') {
        return 'ответ клиента уже разобран прошлым прогоном — повторно не обрабатываем, передано оператору';
    }
    return null;
}
/**
 * D-merge. The generic `OUTCOME_RU.skipped` («узел уже посещался — шаг
 * пропущен») is per-node-idempotency's phrase — wrong when the LIVENESS check
 * in `runner.ts` skipped the node because a merge cancelled the whole run
 * mid-walk (`result.reason === 'run_cancelled'`, see `RunStatusPort`). The
 * operator needs to know the run stopped, not that this one node was already
 * done; `runStatus === null` additionally means the run row itself is gone
 * (self-healed away), not merely cancelled.
 */
function cancelledRunPhraseRu(step) {
    if (step.status !== 'skipped')
        return null;
    const result = isRecord(step.result) ? step.result : null;
    if (result?.reason !== 'run_cancelled')
        return null;
    if (result.runStatus === null)
        return 'прогон удалён — шаг не выполнялся';
    return 'прогон отменён слиянием — шаг не выполнялся';
}
/**
 * Human one-liner for ONE step: `«<тип узла> — <что произошло>»`, e.g.
 * «Отправить шаблон письма — не удалось отправить письмо: Gmail отозвал токен
 * (invalid_grant)» or «Пользователь определён — клиент не опознан».
 *
 * `prefixType: false` drops the node-type head and capitalizes what is left —
 * for the expanded step timeline, which already prints `type · nodeId` on the
 * line above, so the head would just be said twice.
 *
 * Rendered ABOVE the raw `result` JSON, never instead of it: the JSON stays the
 * ground truth for whoever needs ids, the line is for whoever needs the answer.
 * Returns `null` when nothing can be said (unknown status and no cause) so the
 * caller renders just the raw dump rather than an empty line.
 */
export function pipelineStepSummaryRu(step, { prefixType = true } = {}) {
    const phrase = stepPhraseRu(step);
    if (phrase === null)
        return null;
    return prefixType ? `${pipelineNodeTypeLabelRu(step.type)} — ${phrase}` : upperFirst(phrase);
}
/** RU nominal form for a candidate count: 1 совпадение / 2 совпадения / 5 совпадений. */
function matchesRu(count) {
    const mod100 = count % 100;
    const mod10 = count % 10;
    if (mod100 >= 11 && mod100 <= 14)
        return `${count} совпадений`;
    if (mod10 === 1)
        return `${count} совпадение`;
    if (mod10 >= 2 && mod10 <= 4)
        return `${count} совпадения`;
    return `${count} совпадений`;
}
/**
 * D3. The card-digit candidate probe, appended to a `resolve_identity` step.
 * Three outcomes an operator must be able to tell apart: «биллинг таких цифр не
 * знает», «цифры неоднозначны — вот сколько людей под них подходит» and «поиск
 * не отработал». The count NEVER means the customer was identified — four digits
 * cover 3.08 Stripe customers on average, so this line is a lead, not a verdict.
 */
function searchPhraseRu(result) {
    const search = isRecord(result) && isRecord(result.search) ? result.search : null;
    if (!search)
        return null;
    if (search.status === 'unavailable') {
        if (search.reason === 'endpoint_not_deployed') {
            return 'поиск по 4 цифрам карты ещё не подключён';
        }
        if (search.reason === 'name_only_unsupported') {
            return 'поиск только по имени пока не включён';
        }
        return 'поиск по 4 цифрам карты не отработал';
    }
    if (search.status !== 'ok' || typeof search.count !== 'number')
        return null;
    if (search.count === 0)
        return 'по 4 цифрам карты в биллинге совпадений нет';
    // A truncated upstream answer vetoes the unique-candidate bind even at
    // count 1 — say so, or «1 совпадение, нужен оператор» reads as a defect.
    const truncated = search.truncated === true ? ' (выдача усечена биллингом)' : '';
    const total = typeof search.total === 'number' && search.total > search.count ? search.total : null;
    const shown = total !== null ? ` (показаны ${search.count} из ${total})` : '';
    return `по 4 цифрам карты — ${matchesRu(total ?? search.count)}${shown}${truncated}, нужен оператор`;
}
/** What happened at the step, without the node-type head. */
function stepPhraseRu(step) {
    if (isErrorStepStatus(step.status)) {
        // An action the engine could not even attempt — there is no provider cause
        // to quote, the missing adapter IS the cause.
        if (step.status === 'outcome_skipped_no_adapter') {
            return 'шаг не выполнен: интеграция не подключена';
        }
        const what = FAILURE_RU[step.type] ?? 'ошибка';
        const cause = describeStepFailure(step.result);
        return `${what}: ${pipelineCauseRu(cause) ?? cause}`;
    }
    const phrase = (step.type === 'collect_input' ? collectInputPhraseRu(step) : null) ??
        (step.status === 'ok' ? okPhrase(step) : null) ??
        cancelledRunPhraseRu(step) ??
        OUTCOME_BY_TYPE[step.type]?.[step.status] ??
        OUTCOME_RU[step.status] ??
        null;
    // The probe line hangs off the identification step and only ever ADDS to it —
    // an unresolved identification is still the headline.
    const search = step.type === 'resolve_identity' ? searchPhraseRu(step.result) : null;
    if (search === null)
        return phrase;
    return phrase === null ? search : `${phrase}; ${search}`;
}
/**
 * Human one-liner for a whole RUN's failure, derived from its steps first and
 * from `pipeline_run.error` only as a fallback. Returns `null` when the run had
 * no failure to describe.
 *
 * Steps first, and that ordering is the whole point: 109 prod rows carry the OLD
 * `error` format (`failed at node <nodeId>`, the node id alone, no cause) and
 * are NOT backfilled, while another 9 runs finished `completed` with a failed
 * step inside and have `error IS NULL` — no banner at all. The step carries the
 * cause in both cases. Rows written after D15 carry the canonical
 * `${type} @ ${nodeId}: ${cause}` line (`formatRunError`), which the fallback
 * branch parses so a run with no steps loaded still reads in Russian.
 */
export function pipelineRunFailureRu(run) {
    const failedStep = run.steps?.find((s) => isErrorStepStatus(s.status));
    if (failedStep)
        return pipelineStepSummaryRu(failedStep);
    const error = run.error?.trim();
    if (!error)
        return null;
    // Canonical `formatRunError` line: `${type} @ ${nodeId}: ${cause}`.
    const match = /^(\S+) @ (\S+): ([\s\S]+)$/.exec(error);
    const type = match?.[1];
    const cause = match?.[3];
    if (type === undefined || cause === undefined)
        return null;
    const what = FAILURE_RU[type] ?? 'ошибка';
    return `${pipelineNodeTypeLabelRu(type)} — ${what}: ${pipelineCauseRu(cause) ?? cause}`;
}
//# sourceMappingURL=pipeline-labels.js.map